'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { Users, Search, Sparkles } from 'lucide-react';
import { splanApi } from '@/lib/splan-api';
import { useTranslations } from '@/i18n/I18nProvider';

export default function TeachersPage() {
    const [search, setSearch] = useState('');
    const { t } = useTranslations('teachers');

    const { data: instructors, isLoading } = useQuery({
        queryKey: ['instructors'],
        queryFn: () => splanApi.getInstructors(),
    });

    const filteredInstructors = instructors?.filter((instructor) =>
        instructor.name.toLowerCase().includes(search.toLowerCase())
    );

    // Group instructors alphabetically by first letter
    const groupedInstructors = useMemo(() => {
        if (!filteredInstructors) return {};
        const groups = filteredInstructors.reduce(
            (acc, instructor) => {
                const firstLetter = instructor.name.charAt(0).toUpperCase();
                if (!acc[firstLetter]) acc[firstLetter] = [];
                acc[firstLetter].push(instructor);
                return acc;
            },
            {} as Record<string, typeof instructors>
        );
        // Sort instructors within each group
        for (const letter of Object.keys(groups)) {
            groups[letter]?.sort((a, b) => a.name.localeCompare(b.name));
        }
        return groups;
    }, [filteredInstructors]);

    // Get all available first letters for quick navigation
    const availableLetters = useMemo(() => {
        if (!instructors) return [];
        const letters = new Set(instructors.map((i) => i.name.charAt(0).toUpperCase()));
        return Array.from(letters).sort();
    }, [instructors]);

    // Suggested instructors when search is empty
    const suggestedInstructors = useMemo(() => {
        if (!instructors || search.trim()) return [];
        // Get a sample of instructors from different letters
        const byLetter = new Map<string, typeof instructors>();
        for (const instructor of instructors) {
            const letter = instructor.name.charAt(0).toUpperCase();
            if (!byLetter.has(letter)) {
                byLetter.set(letter, []);
            }
            byLetter.get(letter)?.push(instructor);
        }
        // Take first instructor from each letter (up to 8)
        const suggestions: NonNullable<typeof instructors> = [];
        const sortedLetters = Array.from(byLetter.keys()).sort();
        for (const letter of sortedLetters) {
            if (suggestions.length >= 8) break;
            const letterInstructors = byLetter.get(letter);
            if (letterInstructors && letterInstructors[0]) {
                suggestions.push(letterInstructors[0]);
            }
        }
        return suggestions;
    }, [instructors, search]);

    const showSuggestions = !search.trim() && (availableLetters.length > 0 || suggestedInstructors.length > 0);
    const sortedLetters = Object.keys(groupedInstructors).sort();

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f]">
            {/* Sub-header */}
            <div className="bg-white dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white/90 font-heading">
                        {t('teacherLookup')}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-white/50">
                        {t('findCourses')}
                    </p>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search */}
                <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-white/40 w-5 h-5" />
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple/30 dark:focus:ring-purple-bright/30 focus:border-purple dark:focus:border-purple-bright/50"
                    />
                </div>

                {/* Suggestions when no search */}
                {showSuggestions && !isLoading && (
                    <div className="mb-8 space-y-6">
                        {/* Alphabetical quick navigation */}
                        {availableLetters.length > 0 && (
                            <div className="p-4 bg-purple/5 dark:bg-purple-bright/10 rounded-lg border border-purple/10 dark:border-purple-bright/20">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-sm font-medium text-purple dark:text-purple-bright">
                                        {t('browseAZ')}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {availableLetters.map((letter) => (
                                        <button
                                            key={letter}
                                            type="button"
                                            onClick={() => setSearch(letter)}
                                            className="w-8 h-8 text-sm font-medium bg-white dark:bg-white/10 text-gray-700 dark:text-white/80 rounded border border-gray-200 dark:border-white/10 hover:border-purple dark:hover:border-purple-bright hover:text-purple dark:hover:text-purple-bright transition-colors flex items-center justify-center"
                                        >
                                            {letter}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Suggested instructors as cards */}
                        {suggestedInstructors.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <Sparkles className="w-5 h-5 text-purple dark:text-purple-bright" />
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white/90 font-heading">
                                        {t('suggestedTeachers')}
                                    </h2>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {suggestedInstructors.map((instructor) => (
                                        <InstructorCard key={instructor.id} instructor={instructor} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Instructor List */}
                {isLoading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple dark:border-purple-bright mx-auto" />
                        <p className="mt-4 text-gray-500 dark:text-white/50">{t('loadingInstructors')}</p>
                    </div>
                ) : search.trim() ? (
                    // Search results - flat list
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredInstructors?.map((instructor) => (
                            <InstructorCard key={instructor.id} instructor={instructor} />
                        ))}
                    </div>
                ) : (
                    // No search - grouped alphabetically
                    <div className="space-y-8">
                        {sortedLetters.map((letter) => (
                            <div key={letter}>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white/90 mb-4 font-heading flex items-center gap-2">
                                    <span className="w-8 h-8 bg-purple/10 dark:bg-purple-bright/15 rounded-lg flex items-center justify-center text-purple dark:text-purple-bright">
                                        {letter}
                                    </span>
                                </h2>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {groupedInstructors[letter]?.map((instructor) => (
                                        <InstructorCard key={instructor.id} instructor={instructor} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {filteredInstructors?.length === 0 && !isLoading && (
                    <div className="text-center py-12">
                        <Users className="w-12 h-12 text-gray-300 dark:text-white/20 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-white/50">{t('noInstructors')}</p>
                        <p className="text-sm text-gray-400 dark:text-white/40 mt-2">
                            {t('noInstructorsHint')}
                        </p>
                    </div>
                )}
            </main>
        </div>
    );
}

function InstructorCard({ instructor }: { instructor: { id: string; name: string } }) {
    return (
        <Link
            href={`/teachers/${instructor.id}`}
            className="bg-white dark:bg-white/[0.02] p-4 rounded-lg border border-gray-200 dark:border-white/5 hover:border-purple/20 dark:hover:border-purple-bright/20 transition-colors"
        >
            <div className="flex items-center gap-3">
                <div className="p-2 bg-purple/10 dark:bg-purple-bright/15 rounded-lg">
                    <Users className="w-5 h-5 text-purple dark:text-purple-bright" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white/90">
                    {instructor.name}
                </h3>
            </div>
        </Link>
    );
}
